import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createBrandKitSkill: Skill = {
    name: SKILL_NAMES.createBrandKit,
    description: `Load ONLY to BUILD a new brand kit from a website or brand name when none is saved. For using a kit the workspace already has, load ${SKILL_NAMES.applyBrandKit} instead.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Building a Brand Kit

Build a kit only when the user wants one and \`${TOOL_NAMES.listBrandKits}\` shows nothing
suitable. If a kit already exists, apply it instead — see the ${SKILL_NAMES.applyBrandKit} skill.

## Extract
- With a URL, call \`${TOOL_NAMES.extractRawBrandData}\` on it. With only a brand or company
  name, find the real site with \`${TOOL_NAMES.researchTopic}\` first — even for a well-known
  brand, confirm the domain rather than guessing it, since a wrong or parked domain wrecks
  the extraction. (A deliberate exception to the general "don't research things you already
  know" guidance.) Pull 2-3 URLs across brand surfaces (homepage, blog, social profile) —
  they reveal different aspects — then extract them in parallel.

### Validate the extraction first
Check the screenshot and data for a failed extraction: a Cloudflare/bot challenge,
an error or parking page, or a mostly blank/unstyled page. If it looks like
garbage, try alternative URLs (other subdomains, their social pages) — and if
nothing usable comes back, tell the user the site blocked extraction instead of
saving low-confidence data. **No kit beats a wrong one.**

### Logo
Pick the candidate that visually looks like the brand's logo. Reject og-image
social previews, and reject the host platform's own icons when extracting from a
social profile (unless the platform IS the brand). Don't reject a logo for being
small; favicons and apple-touch-icons are a legitimate fallback. Only report "no
logo found" when zero candidates belong to the brand.

### Colors
**The screenshot is ground truth**: pick the 3-5 most prominent, saturated colors
visible in the page design itself (not in photos or ads), then corroborate exact
hex values from the extraction's UI sources and CSS variables. Eliminate CSS
framework defaults (Bootstrap-style red/yellow/green/blue), variables that match
nothing visible, near-duplicate shades, and host-platform colors. Anchors: the
near-black used for text (dark) and the near-white background (light) — brand
colors must be clearly chromatic and distinct from both.

### Fonts
Use the extracted heading and body fonts; ignore generic families (serif,
sans-serif, monospace, system-ui, etc.). Null is fine when nothing real was found.

## Save and apply
Call \`${TOOL_NAMES.saveBrandKit}\` with your curated selections. In the Toolbox this saves
the kit immediately and returns its \`brandKitId\` — apply it right away with
\`${TOOL_NAMES.applyBrandKit}\` so everything built afterwards inherits it.`;
    },
};
